import { CLIENT_METRIC_ROLES, TRANSPORT_COMPANY_REPORT_ROLES } from './roles';

/** Value types returned by GET /api/metricas-operacionais and /api/painel-operacional */
export const OPERATIONAL_METRIC_VALUE_TYPES = {
    SCALAR: 'SCALAR',
    STATUS_COUNT_LIST: 'STATUS_COUNT_LIST',
    TIME_SERIES: 'TIME_SERIES',
    RANKING: 'RANKING',
    MONEY: 'MONEY',
    VOLUME_SERIES: 'VOLUME_SERIES',
    RATE: 'RATE',
};

/** Preset v1 do GET /painel-operacional (ordem da spec). */
export const TRANSPORT_COMPANY_DASHBOARD_PRESET_KEYS = [
    'delivery.created_volume',
    'billing.revenue_in_period',
    'billing.customer_revenue_ranking',
    'delivery.status_count',
    'delivery.first_attempt_success_rate',
];

const TRANSPORT = TRANSPORT_COMPANY_REPORT_ROLES;
const CLIENT = CLIENT_METRIC_ROLES;
const TRANSPORT_AND_CLIENT = [...TRANSPORT, ...CLIENT];

/** Seções do painel operacional, na ordem de exibição. */
export const DASHBOARD_SECTIONS = [
    { id: 'entregas', title: 'Entregas', kpiLabel: 'Indicadores de entregas' },
    { id: 'financeiro', title: 'Financeiro', kpiLabel: 'Indicadores financeiros' },
];

/**
 * Labels, roles and display hints for known metric keys.
 * scalarFormat: integer | percent
 * rankingFormat: integer | money
 * section: entregas | financeiro
 */
export const OPERATIONAL_METRIC_CATALOG = {
    'delivery.created_volume': {
        label: 'Entregas criadas',
        description: 'Quantidade de entregas criadas no período, com volume por dia.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.VOLUME_SERIES,
        allowedRoles: TRANSPORT,
        scalarFormat: 'integer',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'delivery.volume_by_day': {
        label: 'Volume de entregas por dia',
        description: 'Quantidade de entregas criadas por dia no intervalo.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.TIME_SERIES,
        allowedRoles: TRANSPORT_AND_CLIENT,
        scalarFormat: 'integer',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'delivery.status_count': {
        label: 'Entregas por status',
        description: 'Quantidade de entregas em cada status, entre as criadas no período.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.STATUS_COUNT_LIST,
        allowedRoles: TRANSPORT_AND_CLIENT,
        scalarFormat: 'integer',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'delivery.overdue_count': {
        label: 'Entregas atrasadas',
        description: 'Entregas com data agendada no passado e ainda não finalizadas.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.SCALAR,
        allowedRoles: TRANSPORT_AND_CLIENT,
        scalarFormat: 'integer',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'delivery.delivered_count': {
        label: 'Entregas concluídas',
        description: 'Entregas entregues no período.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.SCALAR,
        allowedRoles: CLIENT,
        scalarFormat: 'integer',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'delivery.first_attempt_success_rate': {
        label: 'Sucesso na primeira tentativa',
        description: 'Percentual de entregas finalizadas sem falha ou reagendamento prévio.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.RATE,
        allowedRoles: TRANSPORT,
        scalarFormat: 'percent',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'driver.delivery_count_ranking': {
        label: 'Ranking de entregas por motorista',
        description: 'Motoristas com mais entregas concluídas no período.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.RANKING,
        allowedRoles: TRANSPORT,
        scalarFormat: 'integer',
        rankingFormat: 'integer',
        section: 'entregas',
    },
    'billing.revenue_in_period': {
        label: 'Receita no período',
        description: 'Soma do valor das cobranças das entregas criadas no período, excluindo rascunhos e canceladas.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.MONEY,
        allowedRoles: TRANSPORT,
        scalarFormat: 'integer',
        rankingFormat: 'money',
        section: 'financeiro',
    },
    'billing.customer_revenue_ranking': {
        label: 'Clientes por receita',
        description: 'Ranking de clientes pela receita das entregas criadas no período.',
        valueType: OPERATIONAL_METRIC_VALUE_TYPES.RANKING,
        allowedRoles: TRANSPORT,
        scalarFormat: 'integer',
        rankingFormat: 'money',
        section: 'financeiro',
    },
};

const DEFAULT_META = {
    label: '',
    description: '',
    valueType: '',
    allowedRoles: [],
    scalarFormat: 'integer',
    rankingFormat: 'integer',
};

/**
 * @param {string} key
 * @returns {{ label: string, description: string, valueType: string, allowedRoles: string[], scalarFormat: 'integer' | 'percent', rankingFormat: 'integer' | 'money', section?: 'entregas' | 'financeiro' }}
 */
export function getOperationalMetricMeta(key) {
    const entry = OPERATIONAL_METRIC_CATALOG[key];
    if (entry) {
        return entry;
    }
    return {
        ...DEFAULT_META,
        label: key || '',
    };
}

/**
 * Seção do painel em que a métrica deve aparecer.
 * Chaves desconhecidas de cobrança caem em Financeiro; as demais, em Entregas.
 * @param {string} key
 * @returns {'entregas' | 'financeiro'}
 */
export function resolveMetricDashboardSection(key) {
    const section = OPERATIONAL_METRIC_CATALOG[key]?.section;
    if (section === 'entregas' || section === 'financeiro') {
        return section;
    }
    if (String(key || '').startsWith('billing.')) {
        return 'financeiro';
    }
    return 'entregas';
}

/**
 * Home operational dashboard is currently transport-company only (no /tipos or metric calls for other profiles).
 * @param {string[]} userRoles
 */
export function canAccessOperationalDashboard(userRoles = []) {
    const roles = new Set(userRoles || []);
    return TRANSPORT_COMPANY_REPORT_ROLES.some((role) => roles.has(role));
}

/**
 * Catalog keys the current user may request.
 * @param {string[]} userRoles
 * @returns {string[]}
 */
export function filterCatalogByRoles(userRoles = []) {
    if (!canAccessOperationalDashboard(userRoles)) {
        return [];
    }
    const roles = new Set(userRoles || []);
    return Object.entries(OPERATIONAL_METRIC_CATALOG)
        .filter(([, meta]) => (meta.allowedRoles || []).some((role) => roles.has(role)))
        .map(([key]) => key);
}

/**
 * Intersection of role-allowed keys with keys advertised by GET /tipos, preserving catalog order.
 * @param {string[]} catalogKeys
 * @param {Array<{ key?: string } | string>} apiTypes
 * @returns {string[]}
 */
export function intersectWithApiTypes(catalogKeys = [], apiTypes = []) {
    const available = new Set(
        (apiTypes || [])
            .map((item) => (typeof item === 'string' ? item : item?.key))
            .filter(Boolean)
            .map(String),
    );
    return (catalogKeys || []).filter((key) => available.has(key));
}

/**
 * True when the resolved keys are exactly the v1 transport dashboard preset (any order).
 * @param {string[]} keys
 */
export function isTransportCompanyDashboardPreset(keys = []) {
    if (keys.length !== TRANSPORT_COMPANY_DASHBOARD_PRESET_KEYS.length) {
        return false;
    }
    const resolved = new Set(keys);
    return TRANSPORT_COMPANY_DASHBOARD_PRESET_KEYS.every((key) => resolved.has(key));
}
